import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormFeedback,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import AppModal from "../../../../Components/Common/AppModal";

import CommunicationsSettings from "../communications";
import SmsSender from "./SmsSender";

import {
  addSmsTemplate,
  addSmsTemplateSuccess,
  deleteSmsTemplate,
  deleteSmsTemplateSuccess,
  getSmsTemplateData,
  getSmsTemplateList,
  updateSmsTemplate,
  updateSmsTemplateSuccess,
} from "../../../../store/sms/action";

import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import TableContainer from "../../../../Components/Common/TableContainer";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import LocalDataSearch from "../../../../Components/SearchAndFilter";
import SettingsPanel from "../../components/SettingsPanel";

const emptyForm = {
  Id: "",
  Title: "",
  Body: "",
};

const CreateEditSmsTemplateModal = ({ modal, toggle, isEdit }) => {
  const dispatch = useDispatch();

  const {
    smsTemplateData,
    smsTemplateDataLoading,
    addSmsTemplateLoading,
    updateSmsTemplateLoading,
  } = useSelector((state) => ({
    smsTemplateData: state.SmsReducer?.smsTemplateData,
    smsTemplateDataLoading: state.SmsReducer?.smsTemplateDataLoading,
    addSmsTemplateLoading: state.SmsReducer?.addSmsTemplateLoading,
    updateSmsTemplateLoading: state.SmsReducer?.updateSmsTemplateLoading,
  }));

  const [formData, setFormData] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);

  const saving = addSmsTemplateLoading || updateSmsTemplateLoading;

  useEffect(() => {
    if (!isEdit) {
      setFormData(emptyForm);
      setSubmitted(false);
      return;
    }

    const template = smsTemplateData?.smstemplate_data;

    if (template) {
      setFormData({
        Id: template?.Id || "",
        Title: template?.Title || "",
        Body: template?.Body || "",
      });
    }
  }, [isEdit, smsTemplateData]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isTitleInvalid = submitted && !formData.Title?.trim();
  const isBodyInvalid = submitted && !formData.Body?.trim();

  const handleSubmit = () => {
    setSubmitted(true);

    if (!formData.Title?.trim() || !formData.Body?.trim()) {
      return;
    }

    if (isEdit) {
      dispatch(
        updateSmsTemplate([
          {
            Id: formData.Id,
            Title: formData.Title.trim(),
            Body: formData.Body.trim(),
          },
        ])
      );
    } else {
      dispatch(
        addSmsTemplate([
          {
            Title: formData.Title.trim(),
            Body: formData.Body.trim(),
          },
        ])
      );
    }
  };

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={isEdit ? "Edit SMS Template" : "Create SMS Template"}
      headerAccent="indigo"
      size="md"
      loading={saving}
      onSubmit={handleSubmit}
      submitLabel={isEdit ? "Update Template" : "Create Template"}
      submitColor="primary"
    >
        {smsTemplateDataLoading && isEdit ? (
          <div className="py-5">
            <LoadingSpinner />
          </div>
        ) : (
          <Row>
            <Col lg={12}>
              <div className="mb-3">
                <Label htmlFor="sms-template-title">Title</Label>
                <Input
                  id="sms-template-title"
                  name="Title"
                  value={formData.Title}
                  onChange={handleChange}
                  invalid={isTitleInvalid}
                  placeholder="e.g. Ticket received"
                />
                <FormFeedback>Template title is required.</FormFeedback>
              </div>
            </Col>

            <Col lg={12}>
              <div className="mb-3">
                <Label htmlFor="sms-template-body">SMS Body</Label>
                <Input
                  id="sms-template-body"
                  type="textarea"
                  rows={7}
                  name="Body"
                  value={formData.Body}
                  onChange={handleChange}
                  invalid={isBodyInvalid}
                  placeholder="e.g. Welcome to ESC IT"
                />
                <FormFeedback>SMS body is required.</FormFeedback>

                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">
                    You can use template variables if supported by the sending
                    flow.
                  </small>
                  <small className="text-muted">
                    {formData.Body?.length || 0} characters
                  </small>
                </div>
              </div>
            </Col>
          </Row>
        )}
    </AppModal>
  );
};

export default function SMSSettings() {
  const dispatch = useDispatch();

  const [activeMainTab, setActiveMainTab] = useState("templates");

  const [searchResultList, setSearchResultList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchTab] = useState("all");
  const [showFilter, setShowFilter] = useState(false);
  const [searchInActionShowLoading, setSearchInActionShowLoading] =
    useState(false);

  const [modalTemplate, setModalTemplate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const allowedSearchFilters = ["Id", "Title", "Body"];

  const allowedFiltersInit = [
    {
      useThisFilter: true,
      filterTitle: "Created Date Range",
      filterType: "DateRange",
      filterProperty: "CreatedDateTime",
      filterValue: {
        startDate: "",
        endDate: "",
      },
    },
  ];

  const [allowedFilters, setAllowedFilters] = useState(allowedFiltersInit);

  const {
    smsTemplateListAPI,
    smsTemplateListLoading,
    addSmsTemplateResponse,
    updateSmsTemplateResponse,
    deleteSmsTemplateResponse,
    addSmsTemplateLoading,
    updateSmsTemplateLoading,
    deleteSmsTemplateLoading,
  } = useSelector((state) => ({
    smsTemplateListAPI:
      state.SmsReducer?.smsTemplateList?.smstemplate_list || [],
    smsTemplateListLoading: state.SmsReducer?.smsTemplateListLoading,

    addSmsTemplateResponse: state.SmsReducer?.addSmsTemplate,
    updateSmsTemplateResponse: state.SmsReducer?.updateSmsTemplate,
    deleteSmsTemplateResponse: state.SmsReducer?.deleteSmsTemplate,

    addSmsTemplateLoading: state.SmsReducer?.addSmsTemplateLoading,
    updateSmsTemplateLoading: state.SmsReducer?.updateSmsTemplateLoading,
    deleteSmsTemplateLoading: state.SmsReducer?.deleteSmsTemplateLoading,
  }));

  const toggleTemplate = () => setModalTemplate((prev) => !prev);
  const toggleDelete = () => setDeleteModal((prev) => !prev);
  const toggleShowFilter = () => setShowFilter((prev) => !prev);

  useEffect(() => {
    dispatch(getSmsTemplateList());
  }, [dispatch]);

  const handleCreate = () => {
    setIsEdit(false);
    setModalTemplate(true);
  };

  const handleEdit = (id) => {
    setIsEdit(true);
    setModalTemplate(true);
    dispatch(getSmsTemplateData({ id }));
  };

  const handleDelete = (id) => {
    setDeleteItem(id);
    setDeleteModal(true);
  };

  const confirmDeleteTemplate = () => {
    dispatch(deleteSmsTemplate([deleteItem]));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (isEmpty(addSmsTemplateResponse)) return;

    if (addSmsTemplateResponse?.status === 200) {
      setModalTemplate(false);
      dispatch(getSmsTemplateList());
      toast.success(addSmsTemplateResponse?.message);
    } else {
      toast.error(addSmsTemplateResponse?.message);
    }

    dispatch(addSmsTemplateSuccess({}));
  }, [addSmsTemplateResponse, dispatch]);

  useEffect(() => {
    if (isEmpty(updateSmsTemplateResponse)) return;

    if (updateSmsTemplateResponse?.status === 200) {
      setModalTemplate(false);
      dispatch(getSmsTemplateList());
      toast.success(updateSmsTemplateResponse?.message);
    } else {
      toast.error(updateSmsTemplateResponse?.message);
    }

    dispatch(updateSmsTemplateSuccess({}));
  }, [updateSmsTemplateResponse, dispatch]);

  useEffect(() => {
    if (isEmpty(deleteSmsTemplateResponse)) return;

    if (deleteSmsTemplateResponse?.status === 200) {
      setDeleteModal(false);
      setDeleteItem(null);
      dispatch(getSmsTemplateList());
      toast.success(deleteSmsTemplateResponse?.message);
    } else {
      toast.error(deleteSmsTemplateResponse?.message);
    }

    dispatch(deleteSmsTemplateSuccess({}));
  }, [deleteSmsTemplateResponse, dispatch]);
  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (cellProps) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 ms-2 name">
              #{cellProps.row.original?.Id}
            </div>
          </div>
        ),
      },
      {
        Header: "Title",
        accessor: "Title",
        filterable: true,
      },
      {
        Header: "Body",
        accessor: "Body",
        filterable: true,
        Cell: (cellProps) => {
          const body = cellProps.row.original?.Body || "";

          return (
            <div style={{ maxWidth: 520 }}>
              <span className="text-muted">
                {body.length > 120 ? `${body.slice(0, 120)}...` : body}
              </span>
            </div>
          );
        },
      },
      {
        Header: "Default Invoice",
        accessor: "FlagDefaultSmsInvoice",
        filterable: false,
        Cell: (cellProps) => {
          const value = cellProps.row.original?.FlagDefaultSmsInvoice;

          return value === "1" || value === 1 ? (
            <span className="badge bg-success-subtle text-success">Yes</span>
          ) : (
            <span className="badge bg-light text-muted">No</span>
          );
        },
      },
      {
        Header: "Default Proforma",
        accessor: "FlagDefaultSmsProformaInvoice",
        filterable: false,
        Cell: (cellProps) => {
          const value = cellProps.row.original?.FlagDefaultSmsProformaInvoice;

          return value === "1" || value === 1 ? (
            <span className="badge bg-success-subtle text-success">Yes</span>
          ) : (
            <span className="badge bg-light text-muted">No</span>
          );
        },
      },
      {
        Header: "Action",
        Cell: (cellProps) => (
          <UncontrolledDropdown>
            <DropdownToggle
              className="btn btn-soft-info btn-icon fs-14"
              type="button"
            >
              <i className="fa-solid fa-ellipsis"></i>
            </DropdownToggle>

            <DropdownMenu>
              <DropdownItem
                onClick={() => handleEdit(cellProps.row.original?.Id)}
              >
                <div className="d-flex align-items-center">
                  <i className="ri-pencil-fill align-center text-muted me-1"></i>
                  <span>Edit / View</span>
                </div>
              </DropdownItem>

              <DropdownItem
                onClick={() => handleDelete(cellProps.row.original?.Id)}
              >
                <div className="d-flex align-items-center">
                  <i className="ri-delete-bin-fill align-center text-muted me-1"></i>
                  <span>Delete</span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    []
  );

  const showTable =
    !smsTemplateListLoading &&
    !searchInActionShowLoading &&
    searchResultList?.length > 0;

  const showNoData =
    !smsTemplateListLoading &&
    !searchInActionShowLoading &&
    smsTemplateListAPI?.length === 0;

  const showNoResults =
    !smsTemplateListLoading &&
    !searchInActionShowLoading &&
    smsTemplateListAPI?.length > 0 &&
    searchResultList?.length === 0;

  return (
    <React.Fragment>
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onCloseClick={toggleDelete}
          onDeleteClick={confirmDeleteTemplate}
          customText="Are you sure you want to delete this SMS Template?"
          customButtonText={
            deleteSmsTemplateLoading ? "Deleting..." : "Yes, Delete It!"
          }
        />
      )}

      {modalTemplate && (
        <CreateEditSmsTemplateModal
          modal={modalTemplate}
          toggle={toggleTemplate}
          isEdit={isEdit}
        />
      )}

      <LocalDataSearch
        Page="SmsTemplateSettings"
        ReceivedDataAPI={smsTemplateListAPI}
        setSearchInActionShowLoading={setSearchInActionShowLoading}
        activeSearchTab={activeSearchTab}
        allowedSearchFilters={allowedSearchFilters}
        searchQuery={searchQuery}
        setSearchFilteredResults={setSearchResultList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        toggleShowFilter={toggleShowFilter}
        allowedFiltersInit={allowedFiltersInit}
        allowedFilters={allowedFilters}
        setAllowedFilters={setAllowedFilters}
      />

      <SettingsPanel
        title="SMS Settings"
        description="SMS templates and the channels they send through"
        tabs={[
          {
            value: "templates",
            label: "SMS Templates",
            icon: "ri-message-2-line",
          },
          {
            value: "communications",
            label: "Communications",
            icon: "ri-notification-3-line",
          },
          // Change #100774 - the number texts go out from, and replies to it.
          {
            value: "send-from",
            label: "Send From & Replies",
            icon: "ri-sim-card-2-line",
          },
        ]}
        value={activeMainTab}
        onChange={setActiveMainTab}
      >
        {activeMainTab === "templates" && (
          <Row>
            <Col lg={12}>
              <Card id="smsTemplateList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <Col sm={3}>
                      <div className="search-box">
                        <Input
                          type="search"
                          className="form-control search"
                          placeholder="Search SMS templates..."
                          onChange={handleSearchChange}
                          value={searchQuery}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>

                    <Col sm={9} className="d-flex justify-content-end">
                      <Button color="success" onClick={handleCreate}>
                        + New SMS Template
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody className="pt-0">
                  {smsTemplateListLoading || searchInActionShowLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {showTable && (
                        <TableContainer
                          columns={columns}
                          data={searchResultList}
                          isGlobalFilter={false}
                          isAddUserList={false}
                          customPageSize={10}
                          className="custom-header-css"
                          divClass="table-responsive table-card mb-0"
                          tableClass="align-middle table-wrap"
                          theadClass="text-muted"
                          isLeadsFilter={false}
                        />
                      )}

                      {showNoData && (
                        <NoDataContent
                          titleText="No SMS Templates Found"
                          paragraphText="Create your first SMS template to start sending automated messages."
                          useButton={false}
                        />
                      )}

                      {showNoResults && (
                        <NoDataContent
                          titleText={`No Results for: ${searchQuery}`}
                          paragraphText="Try different search terms or remove filters."
                          useButton={false}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {activeMainTab === "communications" && <CommunicationsSettings />}

        {activeMainTab === "send-from" && <SmsSender />}
      </SettingsPanel>
    </React.Fragment>
  );
}
